import * as RadixSlider from '@radix-ui/react-slider';
import clsx from 'clsx';
import styles from './Slider.module.css';

export interface SliderProps {
  /** Accessible name. */
  readonly label: string;
  readonly value: number;
  readonly min?: number | undefined;
  readonly max: number;
  readonly step?: number | undefined;
  readonly onChange: (value: number) => void;
  /** Fires once when the person releases the thumb (or finishes a key press). */
  readonly onCommit?: ((value: number) => void) | undefined;
  /** A second, quieter fill behind the value, e.g. how much is already loaded. */
  readonly secondaryValue?: number | undefined;
  /** Spoken value, e.g. "12:04 of 1:45:00". */
  readonly valueText?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly className?: string | undefined;
}

export const Slider = ({
  label,
  value,
  min = 0,
  max,
  step = 1,
  onChange,
  onCommit,
  secondaryValue,
  valueText,
  disabled,
  className,
}: SliderProps) => {
  const span = max - min || 1;
  const secondary =
    secondaryValue === undefined ? null : Math.min(1, Math.max(0, (secondaryValue - min) / span));
  return (
    <RadixSlider.Root
      className={clsx(styles.root, className)}
      value={[value]}
      min={min}
      max={max}
      step={step}
      disabled={disabled ?? false}
      onValueChange={([next]) => {
        if (next !== undefined) onChange(next);
      }}
      onValueCommit={([next]) => {
        if (next !== undefined) onCommit?.(next);
      }}
    >
      <RadixSlider.Track className={styles.track}>
        {secondary !== null && <span className={styles.secondary} style={{ width: `${secondary * 100}%` }} />}
        <RadixSlider.Range className={styles.range} />
      </RadixSlider.Track>
      <RadixSlider.Thumb className={styles.thumb} aria-label={label} aria-valuetext={valueText} />
    </RadixSlider.Root>
  );
};
